import { conversionMessage, linkTypeLabel, STATUS_LABELS } from "@/lib/affiliate-ui";
import type { ConversionResult } from "@/lib/hooks/useConvertLink";
import { marketplaceLabel } from "@/lib/marketplace";
import { cn } from "@/lib/utils";

const STATUS_CLASSES: Record<ConversionResult["status"], string> = {
  converted: "border-primary/25 bg-primary/10 text-primary",
  error: "border-destructive/30 bg-destructive/10 text-destructive",
  ignored: "border-border bg-muted/40 text-muted-foreground",
};

export function BulkStatusBadge({ status }: { status: ConversionResult["status"] }): JSX.Element {
  return (
    <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold", STATUS_CLASSES[status])}>
      {STATUS_LABELS[status]}
    </span>
  );
}

/** Tabela de resultado do lote; no celular vira lista empilhada. */
export function BulkResultTable({ items }: { items: readonly ConversionResult[] }): JSX.Element {
  return (
    <ol className="divide-y divide-border rounded-lg border border-border" aria-label="Resultado por link">
      {items.map((item, index) => (
        <li
          key={`${index}-${item.inputUrl}`}
          className="grid gap-2 px-4 py-3 text-sm md:grid-cols-[2rem_7rem_minmax(0,1fr)_minmax(0,1.2fr)_6.5rem] md:items-start"
        >
          <span className="hidden font-mono text-xs text-muted-foreground md:block">{index + 1}</span>
          <span className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            {item.store ? marketplaceLabel(item.store) : "Desconhecida"}
          </span>
          <span className="break-all font-mono text-xs text-muted-foreground" title={item.inputUrl}>
            {item.inputUrl}
          </span>
          <ResultCell item={item} />
          <span className="md:text-right">
            <BulkStatusBadge status={item.status} />
          </span>
        </li>
      ))}
    </ol>
  );
}

function ResultCell({ item }: { item: ConversionResult }): JSX.Element {
  if (item.status === "converted" && item.finalUrl) {
    return (
      <span className="min-w-0 space-y-0.5">
        <span className="block break-all font-mono text-xs text-foreground">{item.finalUrl}</span>
        <span className="block text-[11px] text-muted-foreground">
          {linkTypeLabel(item.linkType)}
          {item.product?.title ? ` · ${item.product.title}` : ""}
        </span>
      </span>
    );
  }
  return (
    <span className={cn("text-xs", item.status === "error" ? "text-destructive" : "text-muted-foreground")}>
      {conversionMessage(item.errorCode)}
    </span>
  );
}
